import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { translations } from "@/i18n";
import { latinLangs } from "@/i18n-latin";
import { germanicLangs } from "@/i18n-germanic";
import { slavicLangs } from "@/i18n-slavic";
import { worldLangs } from "@/i18n-world";

const ALL = { ...translations, ...latinLangs, ...germanicLangs, ...slavicLangs, ...worldLangs };

export const LANGS = [
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
  { code: "ro", label: "Română" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
  { code: "nl", label: "Nederlands" },
  { code: "pl", label: "Polski" },
  { code: "uk", label: "Українська" },
  { code: "ru", label: "Русский" },
  { code: "hu", label: "Magyar" },
  { code: "cs", label: "Čeština" },
  { code: "sv", label: "Svenska" },
  { code: "da", label: "Dansk" },
  { code: "el", label: "Ελληνικά" },
  { code: "ar", label: "العربية" },
  { code: "zh", label: "中文" },
  { code: "ja", label: "日本語" },
  { code: "ko", label: "한국어" },
];

const deepMerge = (base, over) => {
  if (Array.isArray(over)) return over;
  if (over && typeof over === "object") {
    const out = { ...(base && typeof base === "object" ? base : {}) };
    for (const key of Object.keys(over)) out[key] = deepMerge(base ? base[key] : undefined, over[key]);
    return out;
  }
  return over === undefined ? base : over;
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    const saved = localStorage.getItem("mas:lang");
    return ALL[saved] ? saved : "fr";
  });
  const [theme, setTheme] = useState("nuit");

  const setLang = (code) => {
    if (!ALL[code]) return;
    localStorage.setItem("mas:lang", code);
    setLangState(code);
  };

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);

  useEffect(() => {
    if (theme === "parchment") {
      document.documentElement.dataset.theme = "parchment";
    } else {
      delete document.documentElement.dataset.theme;
    }
  }, [theme]);

  const t = useMemo(() => deepMerge(translations.en, ALL[lang] || {}), [lang]);

  const value = { lang, setLang, t, theme, setTheme };
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLang = () => useContext(LanguageContext);
